import { IonPage } from '@ionic/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { HomeMessages } from '../intl/HomeMessages';
import { selectAuthState, selectAuthUserInfo } from '../redux/auth/selectors';
import { AppState } from '../redux/types';
import { englishOnlyIntl, useGA4 } from '../utils/ga4';
import { TosComponent } from '../components/TosComponent';
import { useHistory } from 'react-router';
import { OneButtonModal, OneButtonModalContent } from '../components/OneButtonModal';
import { JobReportGuestAuthMessages } from '../intl/JobReportGuestAuthMessages';
import { useIntl } from 'react-intl';
import { updateTosAcceptanceDate } from '../redux/auth/thunk/updateTosAcceptanceDate';

/**
 * Terms of Service page for Guest.
 *
 * Patterned after TosPage.
 */
export const TosPageForGuest: React.FC = () => {
  document.title = englishOnlyIntl.formatMessage(HomeMessages.reviewTosHeaderGuest);
  useGA4();

  const history = useHistory();
  const { formatMessage } = useIntl();

  const dispatch = useDispatch<ThunkDispatch<AppState, undefined, AnyAction>>();
  const guest = useSelector(selectAuthUserInfo);
  const authState = useSelector(selectAuthState);
  const [isLoading, setLoading] = useState(false);
  const [modalContent, setModalContent] = useState<OneButtonModalContent>(['', '']);

  const emailAddress = guest?.email;
  if (!emailAddress) throw new Error('No user email address exists!');

  const handleAccept = useCallback(async () => {
    setLoading(true);
    dispatch(updateTosAcceptanceDate());
  }, [dispatch]);

  const tosAcceptedAt = useMemo(() => {
    return guest?.tosAcceptedAt;
  }, [guest?.tosAcceptedAt]);

  // Handles the case where something went wrong in the updateTosAcceptanceDate thunk action.
  useEffect(() => {
    if (authState.authSucceeded === false) {
      const errorTitle = formatMessage(JobReportGuestAuthMessages.internalErrorTitle);
      const errorMessage = formatMessage(JobReportGuestAuthMessages.internalErrorMessage);
      setModalContent([errorTitle, errorMessage]);
      setLoading(false);
    }
  }, [authState.authSucceeded, formatMessage]);

  if (tosAcceptedAt) {
    const pathComponents = history.location.pathname.split('/');
    return <Redirect to={`/shared-job-reports/${pathComponents[2]}?sharedWith=${emailAddress}`} />;
  }

  return (
    <IonPage>
      <OneButtonModal
        isOpen={modalContent[0].length > 0}
        onClose={() => setModalContent(['', ''])}
        content={modalContent}
      />
      <TosComponent isLoading={isLoading} handleAccept={handleAccept} />
    </IonPage>
  );
};
